import {
  Close as CloseIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Description as DocumentIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { formatSqlQuery } from "../../utils/sqlFormatter";
import EditIcon from "@mui/icons-material/Edit";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import InputField from "../../components/InputField/InputField";
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useSqlhooks } from "../../hooks/useSqlhooks";
import { useCollaboration } from "../../hooks/useCollaboration";
import ShareModal from "../../components/Share/ShareModal.js";
import { useNavigate } from "react-router-dom";
import { useDatabase } from "../../contexts/DatabaseContext.js";
import { useDbConnection } from "../../hooks/useDbConnection.js";
import { CustomTable } from "../../components/CustomTable/CustomTable.js";
import { useDatabaseConnectionViewModel } from "../../viewmodels/DatabaseConnectionViewModel";
import { useRbac } from "../../hooks/RbacContext";
import {
  THEME,
  MainCard,
  TabsRow,
  TabButtonActive,
  TabButtonInactive,
  EmptyIconBox,
} from "./SavedQuery.style";

const SavedQuery = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const { getSavedQueriesApi, getSharedSavedQueriesApi, deleteSavedQueryApi } = useSqlhooks();
  const { createChannel } = useCollaboration();
  const [tabIndex, setTabIndex] = useState(0);
  const LOCAL_STORAGE_KEY = "selected_user_db_id";
  const [savedQueries, setSavedQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Shared Queries
  const [sharedQueries, setSharedQueries] = useState([]);
  const [loadingShared, setLoadingShared] = useState(true);
  const [errorShared, setErrorShared] = useState(null);

  const [selectedQuery, setSelectedQuery] = useState(null);
  const [showQueryDetails, setShowQueryDetails] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [creatingChannelLoading, setCreatingChannelLoading] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { updateDatabaseState } = useDatabase();
  const { getConnectionDetails, getAllUserDatabases } = useDbConnection();
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(false);

  const [dbDetailsLoading, setDbDetailsLoading] = useState(false);
  const {
    connectionState,
    connect,
    disconnect,
    updateConnectionDetails,
    onError,
  } = useDatabaseConnectionViewModel();
  // For DB connection modal from Topbar
  const [openDbModal, setOpenDbModal] = useState(false);
  const { checkAccess } = useRbac();

  const handleDbModalClose = () => {
    setOpenDbModal(false);
  };

  const handleDbConnectionChange = (field) => (event) => {
    updateConnectionDetails(field, event.target.value);
  };

  const handleDbConnectionSubmit = async () => {
    try {
      await connect(connectionState.connectionDetails);
      // After successful connection, fetch all user databases
      if (userId) {
        setIsLoadingDatabases(true);
        const result = await getAllUserDatabases(userId);
        setDbDetailsLoading(false);

        setIsLoadingDatabases(false);
        if (result.success && result.data && result.data.length > 0) {
          const firstDb = result.data[0];
          setSelectedDatabase(firstDb);
          localStorage.setItem(LOCAL_STORAGE_KEY, firstDb.user_db_id);
          window.dispatchEvent(new Event("selected_user_db_id_changed"));
          // Optionally, fetch connection details for the first DB (if needed)
          if (firstDb.user_db_id) {
            setDbDetailsLoading(true);
            try {
              const detailsResult = await getConnectionDetails(
                userId,
                firstDb.user_db_id
              );
              if (detailsResult.success) {
                let tablesData = detailsResult.data.tablesData;
                if (typeof tablesData === "string") {
                  try {
                    tablesData = JSON.parse(tablesData);
                  } catch (e) {
                    console.error("Error parsing tablesData:", e);
                  }
                }
                const datasourceId = detailsResult.data.user_db_id;
                localStorage.setItem(
                  "snowflake_connection_details",
                  JSON.stringify(detailsResult.data.connectionDetails || {})
                );
                localStorage.setItem(
                  "snowflake_tables_data",
                  JSON.stringify(tablesData)
                );
                if (datasourceId) {
                  localStorage.setItem("snowflake_datasource_id", datasourceId);
                }
                updateDatabaseState({
                  isConnected: true,
                  user_db_id: firstDb.user_db_id,
                  tablesData: tablesData,
                  datasourceId: datasourceId,
                });
              } else {
                localStorage.removeItem("snowflake_connection_details");
                localStorage.removeItem("snowflake_tables_data");
                localStorage.removeItem("snowflake_datasource_id");
                updateDatabaseState({
                  isConnected: false,
                  user_db_id: firstDb.user_db_id,
                  tablesData: null,
                  datasourceId: null,
                });
              }
            } catch (error) {
              console.error(
                "Error fetching connection details for selected DB:",
                error
              );
              localStorage.removeItem("snowflake_connection_details");
              localStorage.removeItem("snowflake_tables_data");
              localStorage.removeItem("snowflake_datasource_id");
              updateDatabaseState({
                isConnected: false,
                user_db_id: firstDb.user_db_id,
                tablesData: null,
                datasourceId: null,
              });
            } finally {
            }
          }
          // Redirect to /dashboard
          navigate("/dashboard");
        }
      }
      handleDbModalClose();
      window.location.reload();
    } catch (error) {
      onError(error);
    }
  };

  const handleDisconnect = () => {
    disconnect();
  };

  const userId = user?.userDetails?.user_id;
  const columns = [
    {
      label: "Query Name",
      id: "query_name",
      render: (value, row) => (
        <Tooltip title="View Query" placement="bottom" arrow>
          <Box
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline dotted' },
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'left',
              gap: 1, //new
              fontWeight: 600,  
              textAlign: 'left',
              width: "180px",
              minWidth: "120px",
              maxWidth: "220px",
              //margin: "0 auto",
            }}
            onClick={() => handleQueryClick(row)}
          >
            <DocumentIcon fontSize="small" sx={{ color: THEME.primary }} />  
            {value}
          </Box>
        </Tooltip>
      ),

      sx: {
        width: "180px",
        minWidth: "120px",
        maxWidth: "220px",
        textAlign: "center",
      },
    },
    {
      label: "Active Channel",
      id: "saved_query_channel",
      render: (value, row) => (
        <Tooltip
          title={value.map((r) => r.channel_name).join(", ")}
          placement="bottom"
          arrow
        >
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ width: "120px", textAlign: "center" }}
          >
            {value.length}
          </Typography>
        </Tooltip>
      ),
      sx: {
        width: "120px",
        minWidth: "80px",
        maxWidth: "150px",
        textAlign: "center",
      },
    },
    {
      label: "Last Modified",
      id: "updated_on",
      render: (value) => (
        <Box
          sx={{
            width: "140px",
            minWidth: "100px",
            maxWidth: "180px",
            textAlign: "center",
            //margin: "0 auto",
          }}
        >
          {value}
        </Box>
      ),
      sx: {
        width: "140px",
        minWidth: "100px",
        maxWidth: "180px",
        textAlign: "center",
      },
    },
  ];
  const fixedColumns = {
    left: [],
    right: ["actions"],
  };

  // Only show actions a user has access to (RBAC)
  const getActions = (query) => {
    const actions = [];

    if (checkAccess("savedQueries", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit Query",
        onClick: () => {
          handleExecuteQuery(query);
        },
        sx: {
          color: THEME.primary,
          "&:hover": {
            color: THEME.hover,
            backgroundColor: THEME.primaryRgba(0.08),
          },
        },
      });
    }

    if (tabIndex === 0 && checkAccess("savedQueries", "share")) {
      actions.push({
        icon: <ShareIcon fontSize="small" />,
        label: "Share Query",
        onClick: () => handleOpenShare(query),
        sx: {
          color: THEME.primary,
          "&:hover": {
            color: THEME.hover,
            backgroundColor: THEME.primaryRgba(0.08),
          },
        },
      });
    }

    if (tabIndex === 0 && checkAccess("savedQueries", "update")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete Query",
        onClick: () => handleOpenDeleteDialog(query),
        sx: {
          color: "#d32f2f",
          "&:hover": {
            color: "#b71c1c",
            backgroundColor: "#ffebee",
          },
        },
      });
    }

    return actions;
  };

  useEffect(() => {
    if (userId && isLoggedIn) {
      fetchSavedQueries();
      fetchSharedQueries();
    }
  }, [userId, isLoggedIn]);

  const fetchSavedQueries = async () => {
    try {
      setLoading(true);

      if (!userId) {
        setError("User not authenticated");
        return;
      }
      const response = await getSavedQueriesApi(userId);

      if (response?.success && response?.data) {
        setSavedQueries(response.data);
        setError(null);
      } else {
        setError("Failed to fetch saved queries");
      }
    } catch (err) {
      setError("Failed to fetch saved queries");
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedQueries = async () => {
    try {
      setLoadingShared(true);
      if (!userId) {
        setErrorShared("User not authenticated");
        return;
      }
      const response = await getSharedSavedQueriesApi(userId);
      if (response?.success && response?.data) {
        setSharedQueries(response.data);
        setErrorShared(null);
      } else {
        setErrorShared("Failed to fetch shared queries");
      }
    } catch (err) {
      setErrorShared("Failed to fetch shared queries");
    } finally {
      setLoadingShared(false);
    }
  };

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setShowQueryDetails(true);
  };

  const handleCloseQueryDetails = () => {
    setShowQueryDetails(false);
    setSelectedQuery(null);
  };

  const handleCopyQuery = (query) => {
    const dbtype =
      query.scenerio_json?.dbtype ||
      query.scenerio_json?.columns?.[0]?.databaseType ||
      query.scenerio_json?.columns?.[0]?.__dbType ||
      "snowflake";
    
    // Clean up the query - remove extra quotes and normalize
    let cleanQuery = query.query || "";
    // Replace triple quotes with single quotes
    cleanQuery = cleanQuery.replace(/"""/g, '"');
    // Replace double quotes with single quotes  
    cleanQuery = cleanQuery.replace(/""/g, '"');
    
    const copyData = {
      query: cleanQuery,
      user_db_id: query.user_db_id,
      db_type: dbtype,
    };
    
    // Use compact JSON (no pretty-printing) for reliable parsing
    navigator.clipboard.writeText(JSON.stringify(copyData));
    setSnackbar({
      open: true,
      message: "Query copied to clipboard!",
      severity: "success",
    });
  };

  const handleExecuteQuery = (query) => {
    const currentDb = localStorage.getItem("selected_user_db_id");
    const dbtype =
      query.scenerio_json?.dbtype ||
      query.scenerio_json?.columns?.[0]?.databaseType ||
      query.scenerio_json?.columns?.[0]?.__dbType ||
      "snowflake";
    const queryWithDbType = {
      ...query,
      scenerio_json: {
        ...query.scenerio_json,
        dbtype: dbtype,
        columns:
          query.scenerio_json?.columns?.map((col) => ({
            ...col,
            databaseType: dbtype,
            __dbType: dbtype,
          })) || [],
      },
    };
    if (query.user_db_id && String(query.user_db_id) !== String(currentDb)) {
      if (currentDb) {
        localStorage.setItem("selected_user_db_id", query.user_db_id);
        updateDatabaseState({
          isConnected: false,
          user_db_id: query.user_db_id,
          tablesData: null,
          datasourceId: null,
          dbtype: dbtype,
        });
        window.dispatchEvent(new Event("selected_user_db_id_changed"));
      }

      (async () => {
        try {
          const detailsResult = await getConnectionDetails(
            userId,
            query.user_db_id
          );
          if (
            detailsResult.data.account &&
            detailsResult.data.tablesData == null
          ) {
            updateConnectionDetails("account", detailsResult.data.account);
            updateConnectionDetails("schema", detailsResult.data.schema);
            updateConnectionDetails("database", detailsResult.data.database);
            if (!openDbModal) setOpenDbModal(true);
            return;
          }
          let tablesData = detailsResult.data.tablesData;
          if (typeof tablesData === "string") {
            try {
              tablesData = JSON.parse(tablesData);
            } catch (e) {
              console.error("Error parsing tablesData:", e);
            }
          }
          const datasourceId = detailsResult.data.user_db_id;
          localStorage.setItem(
            "snowflake_connection_details",
            JSON.stringify(detailsResult.data.connectionDetails || {})
          );
          localStorage.setItem(
            "snowflake_tables_data",
            JSON.stringify(tablesData)
          );
          if (datasourceId) {
            localStorage.setItem("snowflake_datasource_id", datasourceId);
          }
          localStorage.setItem("selected_user_db_type", dbtype);
          updateDatabaseState({
            isConnected: true,
            user_db_id: query.user_db_id,
            tablesData: tablesData,
            datasourceId: datasourceId,
            dbtype: dbtype,
          });
          navigate("/dashboard", { state: { query: queryWithDbType } });
        } catch (err) {
          console.error(
            "Error fetching connection details for saved query DB:",
            err
          );
          localStorage.removeItem("snowflake_connection_details");
          localStorage.removeItem("snowflake_tables_data");
          localStorage.removeItem("snowflake_datasource_id");
          localStorage.removeItem("selected_user_db_type");
          updateDatabaseState({
            isConnected: false,
            user_db_id: query.user_db_id,
            tablesData: null,
            datasourceId: null,
            dbtype: dbtype,
          });
          if (!openDbModal) setOpenDbModal(true);
        }
      })();
    } else {
      navigate("/dashboard", { state: { query: queryWithDbType } });
    }
  };

  const handleOpenShare = useCallback((query) => {
    setShareTarget(query);
    setShareOpen(true);
  }, []);

  const handleCloseShare = useCallback(() => {
    setShareOpen(false);
    setShareTarget(null);
  }, []);

  const handleShareConfirm = useCallback(
    async (users) => {
      setCreatingChannelLoading(true);
      try {
        const result = await createChannel(
          shareTarget?.query_name,
          users,
          shareTarget?.saved_query_id.toString()
        );

        if (result?.success) {
          fetchSavedQueries();
          setSnackbar({
            open: true,
            message: `Successfully shared "${shareTarget?.query_name}" with ${users.length} people(s)`,
            severity: "success",
          });
          handleCloseShare();
          if (result.data?.channel_code) {
            navigate(`/collaboration/${result.data.channel_code}`);
          } else {
            setCreatingChannelLoading(false);
          }
        } else {
          setCreatingChannelLoading(false);
          setSnackbar({
            open: true,
            message: `Failed to share query: ${
              result?.error || "Unknown error"
            }`,
            severity: "error",
          });
          handleCloseShare();
        }
      } catch (error) {
        console.error("Error sharing query:", error);
        setCreatingChannelLoading(false);
        setSnackbar({
          open: true,
          message: `Error sharing query: ${error.message}`,
          severity: "error",
        });
        handleCloseShare();
      }
    },
    [handleCloseShare, shareTarget, createChannel, navigate]
  );

  // Delete handlers
  const handleOpenDeleteDialog = useCallback((query) => {
    setDeleteTarget(query);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget || !userId) return;

    setIsDeleting(true);
    try {
      const result = await deleteSavedQueryApi(
        deleteTarget.saved_query_id,
        userId
      );

      if (result?.success) {
        setSnackbar({
          open: true,
          message: `Successfully deleted "${deleteTarget.query_name}"`,
          severity: "success",
        });
        fetchSavedQueries();
        handleCloseDeleteDialog();
        // Close details dialog if the deleted query was selected
        if (selectedQuery?.saved_query_id === deleteTarget.saved_query_id) {
          setShowQueryDetails(false);
          setSelectedQuery(null);
        }
      } else {
        setSnackbar({
          open: true,
          message: `Failed to delete query: ${result?.error || "Unknown error"}`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting query:", error);
      setSnackbar({
        open: true,
        message: `Error deleting query: ${error.message}`,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, userId, deleteSavedQueryApi, selectedQuery, handleCloseDeleteDialog]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    let date;
    
    // Check if date is in DD/MM/YYYY format
    if (typeof dateString === "string" && dateString.includes("/")) {
      const parts = dateString.split("/");
      if (parts.length === 3) {
        // Parse DD/MM/YYYY format
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        date = new Date(year, month, day);
      } else {
        date = new Date(dateString);
      }
    } else {
      // Try standard date parsing (ISO format, etc.)
      date = new Date(dateString);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if parsing fails
    }
    
    const now = new Date();
    
    // Reset time to midnight for accurate day comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate difference in days
    const diffTime = nowOnly - dateOnly;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays > 1 && diffDays <= 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    }
  };

  const formatFileSize = (sizeInBytes) => {
    const sizeInKB = sizeInBytes / 1024;
    if (sizeInKB < 1024) {
      return `${sizeInKB.toFixed(1)}KB`;
    }
    const sizeInMB = sizeInKB / 1024;
    return `${sizeInMB.toFixed(1)}MB`;
  };

  const getQueryType = (query) => {
    const sql = (query?.query || "").toLowerCase();
    if (sql.includes("select")) return "SELECT";
    if (sql.includes("insert")) return "INSERT";
    if (sql.includes("update")) return "UPDATE";
    if (sql.includes("delete")) return "DELETE";
    if (sql.includes("create")) return "CREATE";
    if (sql.includes("drop")) return "DROP";
    return "QUERY";
  };

  const getQueryTypeColor = () => THEME.primary;

  const paginatedQueries = savedQueries;
  const paginatedSharedQueries = sharedQueries;

  if (!isLoggedIn || !userId) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            textAlign: "center",
            maxWidth: 380,
          }}
        >
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 1.5,
                borderRadius: 12,
                backgroundColor: THEME.primaryRgba(0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DocumentIcon sx={{ fontSize: 32, color: THEME.primary }} />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 0.5, color: "#1e293b", fontSize: "1.25rem" }}
            >
              Welcome Back
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5, fontSize: "0.9rem" }}>
              Sign in to access your saved queries
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="medium"
            onClick={() => (window.location.href = "/login")}
            sx={{
              backgroundColor: THEME.primary,
              borderRadius: 8,
              fontWeight: 600,
              px: 4,
              py: 1.25,
              fontSize: "0.95rem",
              "&:hover": {
                backgroundColor: THEME.hover,
              },
            }}
          >
            Sign In
          </Button>
        </Paper>
      </Box>
    );
  }

  // NEW: Guard entire page with view permission
  if (!checkAccess("savedQueries", "view")) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to view saved queries.
        </Alert>
      </Box>
    );
  }

  // Tab loading/error handling
  const renderTabContent = () => {
    if (tabIndex === 0) {
      if (loading) {
        return (
          <Box sx={{ p: 1.5 }}>
            <LinearProgress sx={{ mb: 1.5, borderRadius: 8 }} />
            <Grid container spacing={2}>
              {[...Array(8)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height={180}
                    sx={{ borderRadius: 8 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      }
      if (error) {
        return (
          <Box sx={{ p: 1.5 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 8,
                "& .MuiAlert-icon": { fontSize: 28 },
                "& .MuiAlert-message": { fontSize: "1rem" },
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={fetchSavedQueries}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        );
      }
      return paginatedQueries.length > 0 ? (
        <CustomTable
          columns={columns}
          data={paginatedQueries}
          showActions={true}
          showAddButton={false}
          showSearch={true}
          showColumnVisibility={false}
          pageSize={25}
          stickyHeader={true}
          fixedColumns={fixedColumns}
          actions={getActions}
          title="My Saved Files"
          subtitle="Access and manage your saved SQL queries"
          alignment="left"
          fontSize="12px"
          showFilters={true}
          compact={true}
          searchKeys={["query_name"]}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            minHeight: 200,
            width: "100%",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <EmptyIconBox>
              <DocumentIcon sx={{ fontSize: 32, color: THEME.primary }} />
            </EmptyIconBox>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#334155",
                mb: 1,
                fontSize: "1rem",
              }}
            >
              No Saved Queries
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748b", fontSize: "0.85rem" }}
            >
              Start by creating your first SQL query.
            </Typography>
          </Box>
        </Box>
      );
    } else if (tabIndex === 1) {
      if (loadingShared) {
        return (
          <Box sx={{ p: 1.5 }}>
            <LinearProgress sx={{ mb: 1.5, borderRadius: 8 }} />
            <Grid container spacing={2}>
              {[...Array(8)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height={180}
                    sx={{ borderRadius: 8 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      }
      if (errorShared) {
        return (
          <Box sx={{ p: 1.5 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 8,
                "& .MuiAlert-icon": { fontSize: 28 },
                "& .MuiAlert-message": { fontSize: "1rem" },
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={fetchSharedQueries}
                >
                  Retry
                </Button>
              }
            >
              {errorShared}
            </Alert>
          </Box>
        );
      }
      return paginatedSharedQueries.length > 0 ? (
        <CustomTable
          columns={columns}
          data={paginatedSharedQueries}
          showActions={true}
          showAddButton={false}
          showSearch={true}
          showColumnVisibility={false}
          pageSize={25}
          stickyHeader={true}
          fixedColumns={fixedColumns}
          actions={getActions}
          title="Shared With Me"
          subtitle="Access SQL queries shared by others"
          alignment="left"
          fontSize="12px"
          showFilters={true}
          compact={true}
          searchKeys={["query_name"]}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            minHeight: 200,
            width: "100%",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <EmptyIconBox>
              <DocumentIcon sx={{ fontSize: 32, color: THEME.primary }} />
            </EmptyIconBox>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#334155",
                mb: 1,
                fontSize: "1rem",
              }}
            >
              No Shared Queries
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748b", fontSize: "0.85rem" }}
            >
              No queries have been shared with you yet.
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <>
    <MainCard sx={{ height: "calc(100vh - 72px)", maxHeight: "calc(100vh - 72px)" }}>
      {/* Tabs for Saved/Shared Queries */}
      <TabsRow>
        {tabIndex === 0 ? (
          <TabButtonActive onClick={() => setTabIndex(0)}>My Saved Query</TabButtonActive>
        ) : (
          <TabButtonInactive onClick={() => setTabIndex(0)}>My Saved Query</TabButtonInactive>
        )}
        {tabIndex === 1 ? (
          <TabButtonActive onClick={() => setTabIndex(1)}>Share with me</TabButtonActive>
        ) : (
          <TabButtonInactive onClick={() => setTabIndex(1)}>Share with me</TabButtonInactive>
        )}
      </TabsRow>

      {/* Tab Content - scrollable with visible scrollbar */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "#f1f5f9", borderRadius: 3 },
          "&::-webkit-scrollbar-thumb": {
            background: THEME.primaryRgba(0.35),
            borderRadius: 3,
            "&:hover": { background: THEME.primaryRgba(0.5) },
          },
        }}
      >
        {renderTabContent()}
      </Box>
    </MainCard>

      {/* Query Details Modal */}
      <Dialog
        open={showQueryDetails}
        onClose={handleCloseQueryDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${THEME.primaryRgba(0.15)}`,
            boxShadow: `0 4px 20px ${THEME.primaryRgba(0.08)}, 0 2px 8px rgba(0,0,0,0.06)`,
            overflow: "hidden",
            maxWidth: 600,
          },
        }}
      >
        {selectedQuery && (
          <>
            <Box
              sx={{
                background: THEME.primary,
                color: "white",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CodeIcon sx={{ fontSize: 16, color: "white" }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: "white",
                    mb: 0.5,
                  }}
                >
                  {selectedQuery.query_name}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.9, fontSize: 11 }}
                  >
                    Created {formatDate(selectedQuery.created_on)}
                  </Typography>
                </Stack>
              </Box>
              <Tooltip title="Copy Query">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyQuery(selectedQuery);
                  }}
                  sx={{
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                  }}
                >
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>

              {/* NEW: Share icon only if user has permission AND on "My Saved Queries" tab */}
              {tabIndex === 0 && checkAccess("savedQueries", "share") && (
                <Tooltip title="Share">
                  <IconButton
                    onClick={() => {
                      handleCloseQueryDetails(); // Close the View Query modal first
                      handleOpenShare(selectedQuery);
                    }}
                    sx={{
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                    size="small"
                  >
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              )}

              <IconButton
                onClick={handleCloseQueryDetails}
                sx={{
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 2.5 }}>
              <Box
                sx={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  p: 1.25,
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    color: THEME.primary,
                    fontWeight: 600,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  SQL Query
                </Typography>
                <Box
                  sx={{
                    background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
                    color: "#e2e8f0",
                    borderRadius: 2,
                    p: 1.25,
                    fontFamily:
                      "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                    fontSize: 12,
                    maxHeight: 200,
                    overflow: "auto",
                    borderLeft: `3px solid ${THEME.primary}`,
                    lineHeight: 1.4,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": {
                      background: "#334155",
                      borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#64748b",
                      borderRadius: 3,
                      "&:hover": { background: "#94a3b8" },
                    },
                  }}
                >
                  {formatSqlQuery(selectedQuery.query)}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                p: 2.5,
                pt: 0,
              }}
            >
              <Button
                onClick={handleCloseQueryDetails}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                  fontWeight: 500,
                  fontSize: 13,
                  borderRadius: 8,
                  px: 2,
                  py: 0.8,
                  textTransform: "none",
                  minWidth: 70,
                  "&:hover": {
                    borderColor: "#cbd5e1",
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                Close
              </Button>

              {/* NEW: Execute allowed only if user has update access */}
              {checkAccess("savedQueries", "update") && (
                <Button
                  variant="contained"
                  onClick={() => handleExecuteQuery(selectedQuery)}
                  size="small"
                  startIcon={<PlayIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    backgroundColor: THEME.primary,
                    color: "white",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 8,
                    px: 2,
                    py: 0.8,
                    textTransform: "none",
                    minWidth: 90,
                    "&:hover": {
                      backgroundColor: THEME.hover,
                    },
                  }}
                >
                  Execute
                </Button>
              )}
            </Box>
          </>
        )}
      </Dialog>

      <Dialog
        open={openDbModal}
        onClose={() => {}}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${THEME.primaryRgba(0.15)}`,
            boxShadow: `0 4px 20px ${THEME.primaryRgba(0.08)}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  borderRadius: 8,
                  backgroundColor: THEME.primaryRgba(0.1),
                  color: THEME.primary,
                }}
              >
                <CloudQueueIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography
                variant="subtitle1"
                component="div"
                sx={{ fontWeight: 600 }}
              >
                Database Connection
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 2.5, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <InputField
              label="Account"
              value={connectionState.connectionDetails.account}
              onChange={handleDbConnectionChange("account")}
              placeholder="Enter account name"
              fullWidth
              size="small"
              disabled={true}
            />
            <InputField
              label="Username"
              value={connectionState.connectionDetails.username}
              onChange={handleDbConnectionChange("username")}
              placeholder="Enter username"
              fullWidth
              size="small"
            />
            <InputField
              label="Password"
              type="password"
              value={connectionState.connectionDetails.password}
              onChange={handleDbConnectionChange("password")}
              placeholder="Enter password"
              fullWidth
              size="small"
            />
            <InputField
              label="Warehouse"
              value={connectionState.connectionDetails.warehouse}
              onChange={handleDbConnectionChange("warehouse")}
              placeholder="Enter warehouse name"
              fullWidth
              size="small"
            />
            <InputField
              label="Role"
              value={connectionState.connectionDetails.role}
              onChange={handleDbConnectionChange("role")}
              placeholder="Enter role"
              fullWidth
              size="small"
            />
            <InputField
              label="Schema"
              value={connectionState.connectionDetails.schema}
              onChange={handleDbConnectionChange("schema")}
              placeholder="Enter schema name"
              fullWidth
              size="small"
              disabled={true}
            />
            <InputField
              label="Database"
              value={connectionState.connectionDetails.database}
              onChange={handleDbConnectionChange("database")}
              disabled={true}
              placeholder="Enter database name"
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <>
            <Button
              onClick={handleDbModalClose}
              color="inherit"
              size="small"
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDbConnectionSubmit}
              variant="contained"
              color="primary"
              startIcon={<CloudDoneIcon sx={{ fontSize: 16 }} />}
              disabled={
                !connectionState.connectionDetails.account ||
                !connectionState.connectionDetails.username ||
                !connectionState.connectionDetails.password ||
                connectionState.loading
              }
              size="small"
              sx={{
                borderRadius: 8,
                textTransform: "none",
                fontWeight: 500,
                backgroundColor: THEME.primary,
                color: "#fff",
                "&:hover": { backgroundColor: THEME.hover },
              }}
            >
              {connectionState.loading ? "Connecting..." : "Connect"}
            </Button>
          </>
        </DialogActions>
      </Dialog>

      {/* Page-level loading while creating channel and redirecting */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: "rgba(255,255,255,0.85)",
        }}
        open={creatingChannelLoading}
      >
        <CircularProgress size={48} sx={{ color: THEME?.primary || "rgb(44, 52, 123)" }} />
      </Backdrop>

      {/* Share Modal */}
      {checkAccess("savedQueries", "share") && (
        <ShareModal
          open={shareOpen}
          onClose={handleCloseShare}
          onShare={handleShareConfirm}
          title={
            shareTarget
              ? `Share "${shareTarget.query_name}"`
              : "Share with people"
          }
          defaultSelectedEmails={[]}
          query_name={shareTarget?.query_name}
          saved_query_id={shareTarget?.saved_query_id}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#d32f2f",
            fontWeight: 600,
          }}
        >
          <DeleteIcon />
          Delete Scenario
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete{" "}
            <strong>"{deleteTarget?.query_name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The scenario and all its data will be
            permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={isDeleting}
            sx={{
              textTransform: "none",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            variant="contained"
            color="error"
            sx={{
              textTransform: "none",
              minWidth: 80,
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 8 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SavedQuery;
